import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { callAPI, hasRoles } from '../components/utils';
import Popover from '@mui/material/Popover';
import Button from '@mui/material/Button';
import {
  Avatar,
  Box,
  Divider,
  Typography,
  Grid,
  IconButton,
  LinearProgress,
  styled,
} from '@mui/material';
import { ExpandMore, Logout, Settings, Update } from '@mui/icons-material';

// Extracts the initials (first and last letter) from a given name in uppercase
const getInitials = (name) => {
  if (typeof name !== 'string' || !name.trim()) return '';

  if (!name) return '';
  const words = name.trim().split(/\s+/);
  const firstInitial = words[0]?.[0] || '';
  const lastInitial = words.length > 1 ? words[words.length - 1][0] : '';
  return (firstInitial + lastInitial).toUpperCase();
};

// Styles the topbar container
const TopbarContainer = styled(Box)(({ theme }) => ({
  backgroundColor: 'var(--white)',
  position: 'sticky',
  top: 0,
  padding: '0.75rem',
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  flexWrap: 'wrap',
  boxShadow: '0 1.5px 1.5px rgba(0, 0, 0, 0.1)',
  zIndex: 1000,
  gap: '0.5rem',
}));

// Menu Button component for buttons on popover
const MenuButton = ({ label, icon, onClick }) => (
  <Button
    variant="text"
    startIcon={icon}
    fullWidth
    onClick={onClick}
    sx={{
      textTransform: 'none',
      justifyContent: 'flex-start',
      fontSize: '1rem',
      p: 0.75,
      borderRadius: 2,
      ':hover': { backgroundColor: 'var(--gray-50)' },
    }}
  >
    {label}
  </Button>
);

const Topbar = ({ isLoggedIn, setLoginStatus }) => {
  const [loggedIn, setLoggedIn] = useState(isLoggedIn);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isTutor, setIsTutor] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const navigate = useNavigate();

  const [userData, setUserData] = useState({
    name: 'No name found',
    email: 'No email found',
    role: 'No role found',
    tutorLevel: 'No level found',
    completedSessions: 0,
    totalSessions: 0,
  });

  useEffect(() => {
    const getUsers = async () => {
      await callAPI({
        request: 'getUsers',
        method: 'get',
        params: {
          self: true,
        },
        onSuccess: (response) => {
          const user = response.data;
          setUserData({
            name: user.name || '',
            email: user.email || '',
            role: user.roles || '',
            tutorLevel: user.level || '',
            completedSessions: user.xp || 0,
            totalSessions:
              user.xp && user.xpRequired
                ? Number(user.xp) + Number(user.xpRequired)
                : '0',
          });
        },
        onError: (errorMessage) => {
          console.error('Failed to fetch user data:', errorMessage);
        },
      });
    };
    getUsers();
  }, []);

  useEffect(() => {
    const checkRoles = async () => {
      setLoggedIn(isLoggedIn);
      try {
        setIsAdmin(hasRoles('Admin'));
        setIsTutor(hasRoles('Tutor'));
        setIsOwner(hasRoles('Owner'));
      } catch (error) {
        console.error('Error fetching user roles:', error);
      }
    };

    checkRoles();
  }, [isLoggedIn]);

  // Popover handlers
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    setLoginStatus(false);
    setLoggedIn(false);
    navigate('/login');
  };

  return (
    <TopbarContainer>
      {/* Tutor Level Progress Bar */}
      {isTutor && !isOwner && (
        <Box sx={{ minWidth: '200px', marginRight: 1 }}>
          <Typography
            variant="h6"
            sx={{ fontSize: '0.9rem' }}
            marginBottom={0.6}
          >
            Tutor Level {userData.tutorLevel}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              position: 'relative',
              width: '100%',
            }}
          >
            <LinearProgress
              variant="determinate"
              value="80"
              sx={{
                width: '100%',
                height: 17,
                borderRadius: 8,
                backgroundColor: 'var(--gray-100)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: 'var(--blue-default)',
                  borderRadius: 8,
                },
              }}
            />
            <Typography
              variant="body2"
              sx={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                fontWeight: 500,
              }}
            >
              {userData.completedSessions}/{userData.totalSessions} Sessions
            </Typography>
          </Box>
        </Box>
      )}

      {/* Audit Logs Button */}
      {/* <IconButton sx={{ backgroundColor: 'var(--gray-100)', padding: 1 }}>
        <Update sx={{ fontSize: '1.5rem', color: 'var(--black)' }} />
      </IconButton> */}

      {/* Account Name Button */}
      <Button
        aria-describedby={id}
        onClick={handleClick}
        variant="text"
        sx={{
          textTransform: 'none',
          justifyContent: 'start',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          borderRadius: 2,
          padding: '0.3rem',
        }}
      >
        <Avatar
          sx={{
            bgcolor: 'var(--blue-light)',
            color: 'var(--navyBlue-default)',
            width: 37,
            height: 37,
            fontSize: '1rem',
            fontWeight: '500',
          }}
        >
          {getInitials(userData.name)}
        </Avatar>
        <Box sx={{ textAlign: 'left' }}>
          <Typography variant="h6" sx={{ fontSize: '0.9rem' }}>
            {userData.name}
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontSize: '0.9rem', color: 'var(--gray-700)' }}
          >
            {userData.role}
          </Typography>
        </Box>
        <ExpandMore sx={{ fontSize: 30, color: 'var(--gray-500)' }} />
      </Button>

      {/* Account Name Pop Over */}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 2,
            p: '1rem',
            mt: 1,
          },
        }}
      >
        <Grid
          sx={{
            minWidth: 200,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            <Avatar
              sx={{
                bgcolor: 'var(--blue-light)',
                color: 'var(--navyBlue-default)',
                width: 50,
                height: 50,
                marginBottom: 1,
                fontSize: '1.25rem',
                fontWeight: '500',
              }}
            >
              {getInitials(userData.name)}
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: '600' }}>
              {userData.name}
            </Typography>
            <Typography variant="body1">{userData.email}</Typography>
            <Typography variant="body1" sx={{ color: 'var(--gray-700)' }}>
              {userData.role}
            </Typography>
          </Box>

          <Divider sx={{ my: 1, width: '95%', mx: 'auto' }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <MenuButton
              label="Settings"
              icon={
                <Settings
                  sx={{
                    marginLeft: '5px',
                    color: 'var(--gray-500)',
                    fontSize: '1.5rem',
                  }}
                />
              }
              onClick={() => navigate('/account')}
            />
            <MenuButton
              label="Log out"
              icon={
                <Logout
                  sx={{
                    marginLeft: '5px',
                    color: 'var(--gray-500)',
                    fontSize: '1.5rem',
                  }}
                />
              }
              onClick={handleLogout}
            />
          </Box>
        </Grid>
      </Popover>
    </TopbarContainer>
  );
};

export default Topbar;
